import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { domainsAPI } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, CheckCircle, XCircle, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const Domains = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState({ domain: '', sendgrid_api_key: '' });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingApiKey, setEditingApiKey] = useState({});
  const [apiKeyValues, setApiKeyValues] = useState({});

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    try {
      const response = await domainsAPI.getAll();
      setDomains(response.data);
    } catch (error) {
      toast.error('Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    try {
      await domainsAPI.create(newDomain);
      toast.success('Domain added successfully');
      setNewDomain({ domain: '', sendgrid_api_key: '' });
      setIsDialogOpen(false);
      loadDomains();
    } catch (error) {
      toast.error('Failed to add domain');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this domain?')) return;
    
    try {
      await domainsAPI.delete(id);
      toast.success('Domain deleted successfully');
      loadDomains();
    } catch (error) {
      toast.error('Failed to delete domain');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-[#F3F4F6] mb-2">
              Domains
            </h1>
            <p className="text-lg text-[#9CA3AF]">
              Manage your email sending domains
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-domain-button"
                className="bg-[#183871] hover:bg-[#1F4BA1] text-white shadow-[0_0_15px_rgba(24,56,113,0.3)]"
              >
                <Plus size={20} className="mr-2" />
                Add Domain
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#13151A] border-[#2A2D36] text-[#F3F4F6]">
              <DialogHeader>
                <DialogTitle className="text-[#F3F4F6]">Add New Domain</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddDomain} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="domain" className="text-[#F3F4F6]">
                    Domain Name
                  </Label>
                  <Input
                    id="domain"
                    data-testid="domain-input"
                    type="text"
                    placeholder="example.com"
                    value={newDomain.domain}
                    onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                    className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="api_key" className="text-[#F3F4F6]">
                    SendGrid API Key (Optional)
                  </Label>
                  <Input
                    id="api_key"
                    data-testid="domain-api-key"
                    type="password"
                    placeholder="SG.xxxxxxxxxx"
                    value={newDomain.sendgrid_api_key}
                    onChange={(e) => setNewDomain({ ...newDomain, sendgrid_api_key: e.target.value })}
                    className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6] font-mono text-sm"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="submit-domain-button"
                  className="w-full bg-[#183871] hover:bg-[#1F4BA1] text-white"
                >
                  Add Domain
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Domains List */}
        {loading ? (
          <p className="text-[#9CA3AF] text-center py-12">Loading domains...</p>
        ) : domains.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#9CA3AF] mb-4">No domains configured yet</p>
            <Button
              onClick={() => setIsDialogOpen(true)}
              className="bg-[#183871] hover:bg-[#1F4BA1] text-white"
            >
              Add Your First Domain
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {domains.map((domain) => (
              <div
                key={domain.id}
                data-testid={`domain-${domain.id}`}
                className="rounded-lg border border-[#2A2D36] bg-[#13151A]/80 backdrop-blur-sm p-6 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.5)]"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-semibold text-[#F3F4F6]">{domain.domain}</h3>
                    {domain.verified ? (
                      <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#10B981]/10 text-[#10B981] text-xs font-medium">
                        <CheckCircle size={14} />
                        Verified
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#EF4444]/10 text-[#EF4444] text-xs font-medium">
                        <XCircle size={14} />
                        Unverified
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={async () => {
                        try {
                          await domainsAPI.update(domain.id, { verified: true });
                          toast.success('Domain marked as verified');
                          loadDomains();
                        } catch (error) {
                          toast.error('Failed to verify domain');
                        }
                      }}
                      data-testid={`verify-domain-${domain.id}`}
                      size="sm"
                      className="bg-[#10B981] hover:bg-[#059669] text-white"
                    >
                      Verify Domain
                    </Button>
                    <Button
                      onClick={() => handleDelete(domain.id)}
                      data-testid={`delete-domain-${domain.id}`}
                      size="sm"
                      variant="outline"
                      className="border-[#2A2D36] text-[#EF4444] hover:bg-[#EF4444]/10"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>

                {/* SendGrid API Key */}
                <div className="mb-4 p-4 rounded-md bg-[#1F2128] border border-[#2A2D36]">
                  <Label className="text-[#F3F4F6] text-sm font-medium mb-2 block">SendGrid API Key</Label>
                  <div className="flex gap-2">
                    <Input
                      type="password"
                      value={apiKeyValues[domain.id] || domain.sendgrid_api_key || ''}
                      onChange={(e) => setApiKeyValues({ ...apiKeyValues, [domain.id]: e.target.value })}
                      placeholder="SG.xxxxxxxxxx"
                      className="flex-1 bg-[#13151A] border-[#2A2D36] text-[#F3F4F6] font-mono text-sm"
                    />
                    <Button
                      onClick={async () => {
                        try {
                          await domainsAPI.update(domain.id, { sendgrid_api_key: apiKeyValues[domain.id] });
                          toast.success('API Key saved');
                          loadDomains();
                        } catch (error) {
                          toast.error('Failed to save API key');
                        }
                      }}
                      size="sm"
                      className="bg-[#183871] hover:bg-[#1F4BA1] text-white"
                    >
                      Save
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* SPF Record */}
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF] text-xs uppercase tracking-wider">SPF Record</Label>
                    <div className="flex items-center gap-2 p-3 rounded-md bg-[#1F2128] border border-[#2A2D36]">
                      <code className="flex-1 text-xs text-[#F3F4F6] font-mono overflow-x-auto">
                        {domain.spf_record}
                      </code>
                      <button
                        onClick={() => copyToClipboard(domain.spf_record)}
                        className="text-[#9CA3AF] hover:text-[#183871]"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>

                  {/* DKIM Record */}
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF] text-xs uppercase tracking-wider">DKIM Record</Label>
                    <div className="flex items-center gap-2 p-3 rounded-md bg-[#1F2128] border border-[#2A2D36]">
                      <code className="flex-1 text-xs text-[#F3F4F6] font-mono overflow-x-auto">
                        {domain.dkim_record.substring(0, 40)}...
                      </code>
                      <button
                        onClick={() => copyToClipboard(domain.dkim_record)}
                        className="text-[#9CA3AF] hover:text-[#183871]"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>

                  {/* CNAME Record */}
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF] text-xs uppercase tracking-wider">CNAME Record</Label>
                    <div className="flex items-center gap-2 p-3 rounded-md bg-[#1F2128] border border-[#2A2D36]">
                      <code className="flex-1 text-xs text-[#F3F4F6] font-mono overflow-x-auto">
                        {domain.cname_record}
                      </code>
                      <button
                        onClick={() => copyToClipboard(domain.cname_record)}
                        className="text-[#9CA3AF] hover:text-[#183871]"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-4 rounded-md bg-[#183871]/10 border border-[#183871]/20">
                  <p className="text-sm text-[#9CA3AF]">
                    Add these DNS records to your domain registrar to verify and authenticate your domain for email sending.
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Domains;
